/**
 * UK Default Lookup Tables for Simple Whole-House Heat Loss
 *
 * Uses a coefficient model rather than full fabric element breakdown.
 * All values are defensible approximations consistent with UK industry practice.
 *
 * Transmission loss  ≈ floorAreaM2 × HLC (W/m²K) × deltaT × exposedFactor
 * Ventilation loss   ≈ volumeM3 × ACH × 0.33 × deltaT
 */

import type { PropertyAgeBand, InsulationLevel, GlazingType, AirTightness, ExposedSides } from './types';

// ─── Heat Loss Coefficient by Age Band (W/m²K at average insulation) ────────

/**
 * Base whole-house heat loss coefficient (W/m²K) by age band.
 * These are per-floor-area figures for a "typical" property in that era.
 * Source: approximate benchmarks aligned with CIBSE TM54 / SAP age-band defaults.
 */
export const BASE_HLC_BY_AGE: Record<PropertyAgeBand, number> = {
  pre_1919:   2.8,
  '1919_1944': 2.5,
  '1945_1964': 2.2,
  '1965_1980': 1.9,
  '1981_1990': 1.6,
  '1991_2002': 1.3,
  '2003_2010': 1.0,
  '2011_plus': 0.75,
  unknown:     2.0,
};

// ─── Insulation Multiplier ───────────────────────────────────────────────────

/**
 * Multiplier applied to the base HLC based on declared insulation level.
 * 'average' = 1.0 (the base value IS average).
 */
export const INSULATION_MULTIPLIER: Record<InsulationLevel, number> = {
  poor:    1.3,
  average: 1.0,
  good:    0.75,
};

// ─── Glazing Adjustment (additive W/m²K on top of base HLC) ─────────────────

/**
 * Additional HLC contribution from glazing type (W/m²K of floor area).
 * Glazing impact is already partially embedded in the age-band base.
 * This nudge adjusts for deliberate overrides.
 */
export const GLAZING_ADJUSTMENT: Record<GlazingType, number> = {
  single: 0.3,
  mixed:  0.1,
  double: 0.0,
  triple: -0.1,
};

// ─── Air Changes Per Hour by Tightness and Age Band ─────────────────────────

/**
 * ACH by air tightness classification.
 * 'normal' calibrated to typical UK stock for the given age band.
 */
export const ACH_BY_TIGHTNESS: Record<AirTightness, Record<PropertyAgeBand, number>> = {
  drafty: {
    pre_1919:   1.4,
    '1919_1944': 1.2,
    '1945_1964': 1.1,
    '1965_1980': 1.0,
    '1981_1990': 0.9,
    '1991_2002': 0.8,
    '2003_2010': 0.7,
    '2011_plus': 0.6,
    unknown:     1.0,
  },
  normal: {
    pre_1919:   0.9,
    '1919_1944': 0.8,
    '1945_1964': 0.75,
    '1965_1980': 0.7,
    '1981_1990': 0.65,
    '1991_2002': 0.6,
    '2003_2010': 0.5,
    '2011_plus': 0.4,
    unknown:     0.65,
  },
  tight: {
    pre_1919:   0.5,
    '1919_1944': 0.45,
    '1945_1964': 0.4,
    '1965_1980': 0.4,
    '1981_1990': 0.35,
    '1991_2002': 0.3,
    '2003_2010': 0.25,
    '2011_plus': 0.2,
    unknown:     0.35,
  },
};

// ─── Exposed-Sides Factor ────────────────────────────────────────────────────

/**
 * Multiplier applied to transmission loss based on the number of exposed sides.
 * 1 exposed side = mid-terrace (lowest exposure)
 * 4 exposed sides = fully detached (highest exposure)
 */
export const EXPOSED_SIDES_FACTOR: Record<ExposedSides, number> = {
  1: 1.00,
  2: 1.10,
  3: 1.18,
  4: 1.25,
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

/**
 * Derive the effective HLC (W/m²K) from inputs.
 * Combines age-band base, insulation multiplier and glazing nudge.
 */
export function deriveHLC(
  ageBand: PropertyAgeBand,
  insulation: InsulationLevel,
  glazing: GlazingType
): number {
  const base = BASE_HLC_BY_AGE[ageBand] ?? BASE_HLC_BY_AGE.unknown;
  const insAdj = INSULATION_MULTIPLIER[insulation] ?? 1.0;
  const glazAdj = GLAZING_ADJUSTMENT[glazing] ?? 0.0;
  return base * insAdj + glazAdj;
}

/**
 * Derive the ACH from air tightness and age band.
 */
export function deriveACH(
  tightness: AirTightness,
  ageBand: PropertyAgeBand
): number {
  const row = ACH_BY_TIGHTNESS[tightness] ?? ACH_BY_TIGHTNESS.normal;
  return row[ageBand] ?? row.unknown;
}

/**
 * Derive the exposed-sides factor (1.0 – 1.25).
 */
export function deriveExposedFactor(exposedSides: ExposedSides): number {
  return EXPOSED_SIDES_FACTOR[exposedSides] ?? 1.0;
}
